/* eslint-disable */

import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { CheckCircle2, Loader2 } from 'lucide-react'
import PostHogTracker from '../../utils/PostHogTracker'
import { get } from '../../http/restservice'
import env from '../../env/env'
import { getUserDetails } from '../../utils/AuthUtil'
import { POSTHOG_EVENTS, POSTHOG_PROPERTIES } from '../../constants/posthogEvents'

import ajathImg from '../../assets/RM/ajath.png'
import jeevaImg from '../../assets/RM/jeeva.png'
import adheipImg from '../../assets/RM/adheip.png'
import pasupathiImg from '../../assets/RM/pasupathi.png'
import fabitsImg from '../../assets/RM/fabits.png'

const RM_IMAGES = [
  { match: 'ajath', src: ajathImg },
  { match: 'jeeva', src: jeevaImg },
  { match: 'jeeav', src: jeevaImg },
  { match: 'adheip', src: adheipImg },
  { match: 'pasupathi', src: pasupathiImg },
]

const getRmImageByName = (name = '') => {
  const normalized = name.toLowerCase()
  const found = RM_IMAGES.find(({ match }) => normalized.includes(match))
  return found?.src || fabitsImg
}

// Component 1: RMMessageButton with Two-Stage Tap Prevention & Neon Glow Styling
const RMMessageButton = ({ onContact }) => {
  const [state, setState] = useState('idle') // 'idle' | 'confirming' | 'loading' | 'success'
  const timerRef = useRef(null)

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  const handleClick = async (e) => {
    e.stopPropagation()
    if (state === 'idle') {
      setState('confirming')
      timerRef.current = setTimeout(() => {
        setState('idle')
      }, 3000) // Reset to idle if no second tap within 3 seconds
    } else if (state === 'confirming') {
      if (timerRef.current) clearTimeout(timerRef.current)
      setState('loading')
      await onContact()
      setState('success')
      setTimeout(() => {
        setState('idle')
      }, 2500)
    }
  }

  // Accent Primary Neon Glow shadow styling mapped to button state
  const buttonStyle = {
    height: '44px',
    boxShadow:
      state === 'confirming'
        ? '0 0 20px rgba(236, 41, 197, 0.65), inset 0 0 8px rgba(236, 41, 197, 0.3)' // Active neon warning glow
        : '0 4px 12px rgba(31, 160, 230, 0.15)',
  }

  return (
    <div
      onClick={handleClick}
      style={buttonStyle}
      className='relative flex-1 overflow-hidden rounded-xl font-semibold text-white select-none cursor-pointer transition-all duration-300'
    >
      <AnimatePresence initial={false} mode='popLayout'>
        {state === 'idle' && (
          <motion.div
            key='idle'
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -40, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className='absolute inset-0 flex items-center justify-center bg-primary-600 hover:bg-primary-700'
          >
            <span>Send Message</span>
          </motion.div>
        )}
        {state === 'confirming' && (
          <motion.div
            key='confirming'
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -40, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className='absolute inset-0 flex items-center justify-center bg-pink-600 hover:bg-pink-700 animate-pulse'
          >
            <span>Confirm Send</span>
          </motion.div>
        )}
        {state === 'loading' && (
          <motion.div
            key='loading'
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -40, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className='absolute inset-0 flex items-center justify-center gap-2 bg-slate-800'
          >
            <Loader2 className='h-4.5 w-4.5 animate-spin text-primary-400' />
            <span>Connecting...</span>
          </motion.div>
        )}
        {state === 'success' && (
          <motion.div
            key='success'
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -40, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className='absolute inset-0 flex items-center justify-center gap-2 bg-emerald-600'
          >
            <CheckCircle2 className='h-4.5 w-4.5 text-white' />
            <span>Message Requested!</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// Component 2: RMVoiceCallButton with Two-Stage Tap Prevention & Glassmorphism Styling
const RMVoiceCallButton = ({ rm, onContact }) => {
  const [state, setState] = useState('idle') // 'idle' | 'confirming' | 'loading' | 'success'
  const timerRef = useRef(null)

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  const handleClick = async (e) => {
    e.stopPropagation()
    if (state === 'idle') {
      setState('confirming')
      timerRef.current = setTimeout(() => {
        setState('idle')
      }, 3000) // Reset to idle after 3 seconds of inactivity
    } else if (state === 'confirming') {
      if (timerRef.current) clearTimeout(timerRef.current)
      setState('loading')

      // Perform contact logging
      await onContact()

      setState('success')

      // Trigger deep-linking action to phone number on the web
      setTimeout(() => {
        window.location.href = `tel:${rm.phone.replace(/\s+/g, '')}`
        setState('idle')
      }, 1500)
    }
  }

  // Dynamic glassmorphic styling based on state
  const glassStyle = {
    height: '44px',
    background:
      state === 'confirming'
        ? 'rgba(239, 68, 68, 0.08)' // Soft red backdrop for confirmation
        : 'rgba(244, 247, 254, 0.65)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    border:
      state === 'confirming'
        ? '1px solid rgba(239, 68, 68, 0.25)'
        : '1px solid rgba(211, 219, 236, 0.5)',
  }

  return (
    <div
      onClick={handleClick}
      style={glassStyle}
      className={`relative flex-1 overflow-hidden rounded-xl font-semibold select-none cursor-pointer transition-all duration-300 ${state === 'confirming' ? 'text-red-700' : 'text-slate-700 hover:bg-slate-100/50'
        }`}
    >
      <AnimatePresence initial={false} mode='popLayout'>
        {state === 'idle' && (
          <motion.div
            key='idle'
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -40, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className='absolute inset-0 flex items-center justify-center'
          >
            <span>Call Advisor</span>
          </motion.div>
        )}
        {state === 'confirming' && (
          <motion.div
            key='confirming'
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -40, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className='absolute inset-0 flex items-center justify-center animate-pulse'
          >
            <span>Confirm Call</span>
          </motion.div>
        )}
        {state === 'loading' && (
          <motion.div
            key='loading'
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -40, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className='absolute inset-0 flex items-center justify-center gap-2 text-slate-600'
          >
            <Loader2 className='h-4.5 w-4.5 animate-spin text-slate-400' />
            <span>Connecting...</span>
          </motion.div>
        )}
        {state === 'success' && (
          <motion.div
            key='success'
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -40, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className='absolute inset-0 flex items-center justify-center gap-2 text-emerald-700 bg-emerald-50/50'
          >
            <CheckCircle2 className='h-4.5 w-4.5 text-emerald-600' />
            <span>Opening Dialer...</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

const DEFAULT_PRIMARY_RM = {
  name: 'Fabits',
  role: 'Personal Finance Partner',
  roleDescription:
    'Handles your portfolio allocation, fund selection, and investment strategy.',
  email: 'ajath.anjanappa@fabits.com',
  phone: '+91 80500 40838',
  type: 'PRIMARY_RM',
}

const DEFAULT_SERVICE_RM = {
  name: 'Jeeva S',
  role: 'Client Success Partner',
  roleDescription:
    'Assists with KYC onboarding, mandates, redemption queries, and support issues.',
  email: 'jeeva.s@fabits.com',
  phone: '+91 80500 40838',
  type: 'SERVICE_RM',
}

const mapManagerToCard = (apiManager, fallback) => {
  if (!apiManager?.managerFullName && !apiManager?.managerPhoneNumber) {
    return {
      ...fallback,
      s3_headshot_url: getRmImageByName(fallback.name),
    }
  }

  const name = (apiManager.managerFullName || fallback.name).trim()

  return {
    ...fallback,
    name,
    email: apiManager.managerEmail || fallback.email,
    phone: apiManager.managerPhoneNumber || fallback.phone,
    s3_headshot_url: getRmImageByName(name),
  }
}

const DedicatedWealthTeam = () => {
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' })
  const [customerDetails, setCustomerDetails] = useState(null)

  const fetchCustomerKycDetails = async (phNum) => {
    if (!phNum) return null

    const phNo = '%2b91' + phNum?.replace(/^(\+91\s?)/, '')
    const response = await get(`${env.API_URL}customerservice/api/customer?phoneNumber=${phNo}`)

    if (!response || response?.isError) {
      return null
    }

    return response
  }

  useEffect(() => {
    const loadCustomerDetails = async () => {
      const userDetails = getUserDetails()
      const phoneNumber = userDetails?.phoneNumber

      if (!phoneNumber) return

      const details = await fetchCustomerKycDetails(phoneNumber)
      if (details) {
        setCustomerDetails(details)
      }
    }

    loadCustomerDetails()
  }, [])

  const hasApiPrimaryRm = !!(
    customerDetails?.rm?.managerFullName || customerDetails?.rm?.managerPhoneNumber
  )
  const hasApiServiceRm = !!(
    customerDetails?.serviceRm?.managerFullName ||
    customerDetails?.serviceRm?.managerPhoneNumber
  )
  const hasApiTeam = hasApiPrimaryRm || hasApiServiceRm

  const teamCards = hasApiTeam
    ? [
      ...(hasApiPrimaryRm
        ? [['primaryRM', mapManagerToCard(customerDetails.rm, DEFAULT_PRIMARY_RM)]]
        : []),
      ...(hasApiServiceRm
        ? [['serviceRM', mapManagerToCard(customerDetails.serviceRm, DEFAULT_SERVICE_RM)]]
        : []),
    ]
    : [['primaryRM', mapManagerToCard(null, DEFAULT_PRIMARY_RM)]]

  const showToastNotification = (message, type = 'success') => {
    setToast({ show: true, message, type })
    setTimeout(() => {
      setToast((prev) => ({ ...prev, show: false }))
    }, 4000)
  }

  const executeContactLog = async (rmDetails, actionType) => {
    const phone = (rmDetails.phone || '').replace(/\D/g, '')
    const eventProps = {
      [POSTHOG_PROPERTIES.ADVISOR_NAME]: rmDetails.name,
      [POSTHOG_PROPERTIES.ADVISOR_TYPE]: rmDetails.type,
    }

    if (actionType === 'MESSAGE') {
      PostHogTracker.track(POSTHOG_EVENTS.SEND_MESSAGE_CLICKED, eventProps)

      if (phone) {
        window.open(`https://wa.me/${phone}`, '_blank', 'noopener,noreferrer')
        showToastNotification(`Opening WhatsApp with ${rmDetails.name}.`, 'success')
      }
      return
    }

    if (actionType === 'VOICE_CALL') {
      PostHogTracker.track(POSTHOG_EVENTS.CALL_ADVISOR_CLICKED, eventProps)
      showToastNotification(`Connecting you with ${rmDetails.name}.`, 'success')
    }
  }

  const renderCard = (key, rm) => {
    return (
      <motion.div
        key={key}
        className='flex-1 flex flex-col justify-between relative overflow-hidden rounded-2xl border border-slate-100 bg-white p-4 shadow-sm transition-all duration-300 hover:shadow-md'
        style={{
          border: '1px solid rgba(226, 232, 240, 0.8)',
          background:
            'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(250, 252, 255, 1) 100%)',
        }}
        whileHover={{ y: -3, transition: { duration: 0.2 } }}
      >
        {/* Subtle decorative background gradient */}
        <div className='absolute top-0 right-0 -mr-16 -mt-16 h-32 w-32 rounded-full bg-gradient-to-tr from-sky-100/30 to-purple-100/30 blur-2xl pointer-events-none' />

        <div className='flex flex-row items-start gap-3 sm:gap-4 mb-3.5'>
          {/* Avatar Area with dynamic image rendering */}
          <div className='relative shrink-0'>
            <div className='h-12 w-12 overflow-hidden rounded-full border-2 border-white shadow-sm bg-slate-100'>
              <img
                src={rm.s3_headshot_url}
                alt={rm.name}
                className='h-full w-full object-cover'
                onError={(e) => {
                  e.target.onerror = null
                  e.target.src = fabitsImg
                }}
              />
            </div>
            {/* Green Online Dot */}
            <div className='absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white bg-emerald-500' />
          </div>

          {/* Details & Roles */}
          <div className='flex-1 min-w-0 text-left space-y-1'>
            <div className='flex items-center flex-wrap gap-2'>
              <h3 className='font-semibold text-slate-800 text-sm lg:text-base'>{rm.name}</h3>
              <span className='inline-flex items-center rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700 border border-primary-100'>
                {key === 'primaryRM' ? 'Strategy' : 'Operations'}
              </span>
            </div>
            <p className='text-xs font-medium text-slate-500 uppercase tracking-wider'>{rm.role}</p>
            <p className='text-xs text-slate-600 font-light pr-2 leading-relaxed'>
              {rm.roleDescription}
            </p>
          </div>
        </div>

        <div>
          {/* Separator */}
          <hr className='my-3 border-slate-100' />

          {/* Interactive Action Buttons */}
          <div className='flex flex-row gap-2.5 w-full'>
            <RMVoiceCallButton rm={rm} onContact={() => executeContactLog(rm, 'VOICE_CALL')} />
            <RMMessageButton rm={rm} onContact={() => executeContactLog(rm, 'MESSAGE')} />
          </div>
        </div>
      </motion.div>
    )
  }

  return (
    <div className='w-full lg:max-w-[1128px] mx-auto mt-6'>
      {/* Toast Notification */}
      <AnimatePresence>
        {toast.show && (
          <div className='fixed bottom-6 inset-x-0 z-50 flex justify-center pointer-events-none px-4'>
            <motion.div
              initial={{ opacity: 0, y: 50, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              className='pointer-events-auto flex items-center gap-3 rounded-xl bg-slate-900 px-5 py-4 text-white shadow-xl max-w-sm w-full sm:w-auto'
            >
              <CheckCircle2 className='h-5 w-5 text-emerald-400 shrink-0' />
              <p className='text-sm font-medium'>{toast.message}</p>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <div className='mb-4 flex items-center justify-between'>
        <div>
          <h2 className='text-lg font-semibold text-slate-900'>Your Dedicated Team</h2>
          <p className='text-sm text-slate-500 font-light'>
            Get in touch with your wealth managers.
          </p>
        </div>
        <div className='hidden sm:flex items-center gap-1.5 text-xs font-medium text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-full px-2.5 py-1'>
          <span className='relative flex h-2 w-2'>
            <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75'></span>
            <span className='relative inline-flex rounded-full h-2 w-2 bg-emerald-500'></span>
          </span>
          Online
        </div>
      </div>

      <div className='flex flex-col md:flex-row gap-6'>
        {teamCards.map(([key, rm]) => renderCard(key, rm))}
      </div>
    </div>
  )
}

export default DedicatedWealthTeam
